import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Upload,
  Description,
  AutoAwesome,
  Visibility,
  GetApp,
  Close
} from "@mui/icons-material";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import * as XLSX from "xlsx";
import { Button } from "common/components";
import HeaderNew from "../../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../../common/components/custom/hub-header/HubHeader";
import { ToastMessageUtil } from "../../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../../common/enums/controls/toast.enum";
import { MainRoutes } from "../../../../../common/config";
import {
  TagDefinition,
  TagLibraryConfig,
  DropdownOption
} from "../../../data/models/configuration-hub.model";
import {
  useAppDispatch,
  useAppSelector
} from "../../../../../common/redux/core";
import { ConfigurationHubService } from "../../../data/services/configuration-hub.service";
import sampleFile from "../../../../../assets/sample-files/Sample_format.xlsx";
import "./CreateTagLibraryConfig.scss";

type Phase = "setup" | "enrich" | "embed" | "complete";

interface LocationState {
  tags?: TagDefinition[];
  configName?: string;
  categoryName?: string;
  categoryDescription?: string;
  phase?: Phase;
  editMode?: boolean;
  guidCode?: string;
  configurationId?: number;
  documentType?: string;
  configData?: any;
}

const CreateTagLibraryConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State management
  const [currentPhase, setCurrentPhase] = useState<Phase>("setup");
  const [configName, setConfigName] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>("");
  const [categoryDescription, setCategoryDescription] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [tags, setTags] = useState<TagDefinition[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [showSamplePreview, setShowSamplePreview] = useState<boolean>(false);
  const [sampleData, setSampleData] = useState<string[][]>([]);
  const [sampleHeaders, setSampleHeaders] = useState<string[]>([]);

  const loadSampleData = useCallback(async () => {
    try {
      const response = await fetch(sampleFile);
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: string[][] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1
      });
      if (jsonData.length > 0) {
        setSampleHeaders(jsonData[0].map(String));
        setSampleData(jsonData.slice(1, 11)); // Show first 10 rows
      }
    } catch (error) {
      console.error("Error loading sample file:", error);
    }
  }, []);

  const handleViewSample = () => {
    if (sampleHeaders.length === 0) {
      loadSampleData();
    }
    setShowSamplePreview(true);
  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = sampleFile;
    link.download = "Sample_format.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle incoming navigation state - only initialize once to prevent overwriting user edits
  useEffect(() => {
    if (locationState && !isInitialized) {
      if (locationState.tags) setTags(locationState.tags);
      if (locationState.configName) setConfigName(locationState.configName);
      if (locationState.categoryName)
        setCategoryName(locationState.categoryName);
      if (locationState.categoryDescription)
        setCategoryDescription(locationState.categoryDescription);
      if (locationState.phase) setCurrentPhase(locationState.phase);
      setIsInitialized(true);
    }
  }, [locationState, isInitialized]);

  const handleUploadAndProcess = async () => {
    // In edit mode, we don't need to upload files - update existing config and skip to enrichment
    if (locationState?.editMode) {
      if (!configName || !categoryName) {
        ToastMessageUtil.show(
          "Please fill all required fields",
          ToastVariant.WARNING
        );
        return;
      }

      if (!locationState.guidCode) {
        ToastMessageUtil.show(
          "Configuration ID missing - cannot update",
          ToastVariant.ERROR
        );
        return;
      }

      // Trim whitespace and validate non-empty values
      const trimmedConfigName = configName.trim();
      const trimmedCategoryName = categoryName.trim();
      const trimmedCategoryDescription = categoryDescription.trim();

      if (!trimmedConfigName || !trimmedCategoryName) {
        ToastMessageUtil.show(
          "All fields must contain valid text (not just spaces)",
          ToastVariant.WARNING
        );
        return;
      }

      setIsProcessing(true);

      try {
        // Prepare update data with trimmed values - use lowercase field names as API expects
        const updateData = {
          name: trimmedCategoryName, // Tag Category Name maps to name
          description: trimmedCategoryDescription, // Tag Category Description maps to description
          configurationName: trimmedConfigName // Configuration Name maps to configurationName
        };

        // Call the service method to update the configuration
        const result = await ConfigurationHubService.updateTagConfigCardUpdate(
          locationState.guidCode,
          updateData
        );

        if (result.error) {
          throw new Error(
            result.error.data?.join(", ") || "Failed to update configuration"
          );
        }

        ToastMessageUtil.show(
          "Configuration updated successfully!",
          ToastVariant.SUCCESS
        );

        // Navigate to enrichment phase with updated values
        navigate(MainRoutes.TAG_ENRICHMENT_PHASE, {
          state: {
            tags: locationState.tags || [],
            configName: trimmedConfigName, // Use updated trimmed value
            categoryName: trimmedCategoryName, // Use updated trimmed value
            categoryDescription: trimmedCategoryDescription, // Use updated trimmed value
            fromSetup: true,
            editMode: true,
            guidCode: locationState.guidCode,
            configurationId: locationState.configurationId,
            masterObjectId: locationState.configurationId
          }
        });
      } catch (error: any) {
        console.error("Error updating configuration:", error);
        ToastMessageUtil.show(
          `Failed to update configuration: ${error.message}`,
          ToastVariant.ERROR
        );
      } finally {
        setIsProcessing(false);
      }

      return;
    }

    // Original upload logic for new configurations
    if (!configName || !categoryName || !uploadedFile) {
      ToastMessageUtil.show(
        "Please fill all required fields and upload a file",
        ToastVariant.WARNING
      );
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      // Start progress simulation while API call is in progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90; // Stop at 90% until API completes
          }
          return prev + 10;
        });
      }, 200);

      // Make the actual API call
      const result = await ConfigurationHubService.uploadTagLibrary(
        uploadedFile,
        categoryName,
        configName, // Using configName as configuration_name
        categoryDescription
      );

      clearInterval(progressInterval);

      if (!result.error && result.data) {
        // Complete the progress
        setProgress(100);

        // Extract masterObjectId from API response
        const masterObjectId = result.data.masterObjectId;

        // For backward compatibility, still extract any tags that might be in the response
        let extractedTags: TagDefinition[] = [];

        if (result.data.tags && Array.isArray(result.data.tags)) {
          extractedTags = result.data.tags.map((tag: any, index: number) => ({
            id: tag.id || (index + 1).toString(),
            tagName: tag.tagName || tag.name || `Tag ${index + 1}`,
            parentTagName: tag.parentTagName || tag.parent || "Root",
            tagDefinition:
              tag.tagDefinition ||
              tag.definition ||
              `Tag definition for ${tag.tagName || tag.name}`,
            cfihosTagId:
              tag.cfihosTagId ||
              tag.cfihosId ||
              `TAG-${String(index + 1).padStart(3, "0")}`
          }));
        }

        setTags(extractedTags);
        setIsProcessing(false);

        // Navigate to enrichment phase with masterObjectId
        navigate(MainRoutes.TAG_ENRICHMENT_PHASE, {
          state: {
            tags: extractedTags,
            configName,
            categoryName,
            categoryDescription,
            fromSetup: true,
            masterObjectId: masterObjectId
          }
        });

        ToastMessageUtil.show(
          "File uploaded and processed successfully!",
          ToastVariant.SUCCESS
        );
      } else {
        // Handle API error response
        const errorMessage = result.error?.data?.length
          ? result.error.data.join(", ")
          : "Failed to upload file";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      setIsProcessing(false);
      setProgress(0);

      ToastMessageUtil.show(
        error.message || "Failed to upload and process file. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  const handleProcessEmbedding = () => {
    if (tags.length === 0) {
      ToastMessageUtil.show(
        "No tags available to process",
        ToastVariant.WARNING
      );
      return;
    }

    setCurrentPhase("embed");
    setIsProcessing(true);
    setProgress(0);

    // Simulate embedding processing
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);

          // Navigate to completion page with configuration data
          navigate(MainRoutes.COMPLETE_TAG_LIBRARY_CONFIG, {
            state: {
              configName,
              categoryName,
              categoryDescription,
              tags
            }
          });
          return 100;
        }
        return prev + 8;
      });
    }, 150);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (
      file &&
      (file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".csv"))
    ) {
      setUploadedFile(file);
    } else {
      ToastMessageUtil.show(
        "Please select a valid Excel (.xlsx, .xls) or CSV file",
        ToastVariant.ERROR
      );
    }
  };

  const handleSaveConfiguration = () => {
    const newConfig: TagLibraryConfig = {
      id: Date.now(),
      guidCode: `guid-${Date.now()}`,
      name: configName,
      description: categoryDescription || "",
      cfiHOSId: `CFI-${Date.now()}`,
      isCFIHOS: false,
      order: 1,
      isActive: true,
      configurationName: configName,
      totalTagsProcessed: tags.length,
      createdBy: "Current User",
      createdDate: new Date().toISOString(),
      updatedBy: "Current User",
      updatedDate: new Date().toISOString()
    };

    // Save to localStorage for demo purposes
    try {
      const saved = localStorage.getItem("tagLibraryConfigurations");
      const configs = saved ? JSON.parse(saved) : [];
      configs.push(newConfig);
      localStorage.setItem("tagLibraryConfigurations", JSON.stringify(configs));

      ToastMessageUtil.show(
        "Tag library configuration created successfully",
        ToastVariant.SUCCESS
      );
      navigate(MainRoutes.TAG_LIBRARY_CONFIG);
    } catch (error) {
      ToastMessageUtil.show("Failed to save configuration", ToastVariant.ERROR);
    }
  };

  const renderProgressTracker = () => {
    const phases = [
      { key: "setup", label: "Setup", icon: <Upload /> },
      { key: "enrich", label: "Enrich", icon: <AutoAwesome /> },
      { key: "complete", label: "Complete", icon: <DoneOutlinedIcon /> }
    ];

    const getPhaseIndex = (phase: string) =>
      phases.findIndex((p) => p.key === phase);
    const currentIndex = getPhaseIndex(currentPhase);

    const isPhaseActive = (phaseKey: string) => currentPhase === phaseKey;
    const isPhaseCompleted = (phaseKey: string) => {
      const phaseIndex = getPhaseIndex(phaseKey);
      return phaseIndex < currentIndex || currentPhase === "complete";
    };

    return (
      <div className="progress-tracker">
        <div className="progress-steps">
          {phases.map((phase, index) => (
            <div
              key={phase.key}
              className={`progress-step ${
                isPhaseCompleted(phase.key) ? "completed" : ""
              }`}
            >
              <div
                className={`step-icon ${
                  isPhaseActive(phase.key) || isPhaseCompleted(phase.key)
                    ? "active"
                    : "inactive"
                }`}
              >
                {phase.icon}
              </div>
              <div className="step-label">{phase.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSetupPhase = () => (
    <div className="config-card">
      <div className="card-header">
        <h2>Setup Tag Library Configuration</h2>
      </div>

      <div className="form-section">
        <div className="form-group">
          <label className="tool-development__label">
            Configuration Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="input"
            placeholder="e.g. CFIHOS, ISO, Custom"
            value={configName}
            onChange={(e) => setConfigName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="tool-development__label">
            Tag Category Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="input"
            placeholder="e.g. Discipline, Equipment Class"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="tool-development__label">
            Tag Category Description
            {/* <span className="required">*</span> */}
          </label>
          <textarea
            className="input tool-development__textarea"
            placeholder="Describe the tag category"
            value={categoryDescription}
            onChange={(e) => setCategoryDescription(e.target.value)}
            rows={4}
          />
        </div>

        {/* Only show file upload section when not in edit mode */}
        {!locationState?.editMode && (
          <>
            <div className="sample-file-section">
              <div className="sample-file-card">
                <div className="sample-file-info">
                  <div className="sample-file-icon-wrapper">
                    <Description className="sample-file-icon" />
                  </div>
                  <div className="sample-file-text">
                    <span className="sample-file-title">
                      Sample_format.xlsx
                    </span>
                    <span className="sample-file-hint">
                      Need help with the format? Preview or download the sample
                      template below
                    </span>
                  </div>
                </div>
                <div className="sample-file-actions">
                  <button
                    className="sample-btn sample-btn--view"
                    onClick={handleViewSample}
                  >
                    <Visibility fontSize="small" />
                    Preview Format
                  </button>
                  <button
                    className="sample-btn sample-btn--download"
                    onClick={handleDownloadSample}
                  >
                    <GetApp fontSize="small" />
                    Download Template
                  </button>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>
                Upload Excel/CSV File <span className="required">*</span>
              </label>
              <div
                className={`file-upload-area ${uploadedFile ? "has-file" : ""}`}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadedFile ? (
                  <div className="file-info">
                    <Description className="file-icon" />
                    <div className="file-details">
                      <div className="file-name">{uploadedFile.name}</div>
                      <div className="file-size">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="upload-icon" />
                    <div className="upload-text">
                      Click to upload Excel file
                    </div>
                    <div className="upload-hint">
                      Supports .xlsx, .xls, .csv
                    </div>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </>
        )}

        {isProcessing && (
          <div className="progress-section">
            <div className="progress-info">
              <span className="progress-label">Processing file...</span>
              <span className="progress-percent">{progress}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="card-actions">
        <div className="action-left"></div>
        <div className="action-right">
          <Button
            variant="primary"
            onClick={handleUploadAndProcess}
            disabled={
              !configName ||
              !categoryName ||
              // Only require upload when not in edit mode
              (!locationState?.editMode && !uploadedFile) ||
              isProcessing
            }
          >
            {isProcessing
              ? "Processing..."
              : locationState?.editMode
              ? "Continue"
              : "Upload & Process"}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case "setup":
        return renderSetupPhase();
      case "enrich":
        // Redirect to dedicated enrichment page
        navigate(MainRoutes.TAG_ENRICHMENT_PHASE, {
          state: {
            tags,
            configName,
            categoryName,
            categoryDescription,
            fromSetup: true
          }
        });
        return renderSetupPhase(); // Fallback while redirecting
      // case "embed":
      //   return renderEmbedPhase();
      case "complete":
        // Redirect to dedicated completion page
        navigate(MainRoutes.COMPLETE_TAG_LIBRARY_CONFIG, {
          state: {
            configName,
            categoryName,
            categoryDescription,
            tags
          }
        });
        return renderSetupPhase(); // Fallback while redirecting
      default:
        return renderSetupPhase();
    }
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="create-tag-library-container">
        <HubHeader
          className="header"
          title="Tag Library Configurations"
          description="Manage tag libraries with AI-powered enrichment and embeddings"
          showBackButton={true}
          backRoute="/tag-library-config"
        />
        <div className="container">
          {renderProgressTracker()}
          {renderCurrentPhase()}
        </div>
      </div>

      {/* Sample File Preview Modal */}
      {showSamplePreview && (
        <div
          className="sample-modal-overlay"
          onClick={() => setShowSamplePreview(false)}
        >
          <div className="sample-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sample-modal__header">
              <div className="sample-modal__title">
                <Description />
                <span>Sample_format.xlsx</span>
              </div>
              <div className="sample-modal__header-actions">
                <button
                  className="sample-modal__download-btn"
                  onClick={handleDownloadSample}
                >
                  <GetApp fontSize="small" />
                  Download
                </button>
                <button
                  className="sample-modal__close"
                  onClick={() => setShowSamplePreview(false)}
                >
                  <Close />
                </button>
              </div>
            </div>
            <div className="sample-modal__body">
              {sampleHeaders.length > 0 ? (
                <div className="sample-table-wrapper">
                  <table className="sample-table">
                    <thead>
                      <tr>
                        {sampleHeaders.map((header, i) => (
                          <th key={i}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sampleData.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {sampleHeaders.map((_, colIdx) => (
                            <td key={colIdx}>{row[colIdx] ?? ""}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="sample-modal__loading">Loading preview...</div>
              )}
              {sampleData.length > 0 && (
                <div className="sample-modal__footer-note">
                  Showing first {sampleData.length} rows of the sample file
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateTagLibraryConfig;
